import { apiClient } from './client';

// Mirrors packages/api/src/modules/gtm-sourcing/ (the web app keeps its own copy,
// as gtm.ts, signalOps.ts and funnel.ts do).

export type SourceType =
  | 'tender_win'
  | 'framework_panel'
  | 'tender_lost'
  | 'job_posting'
  | 'website_visit'
  | 'manual';

export const SOURCE_LABELS: Record<string, string> = {
  tender_win: 'Won public tenders',
  framework_panel: 'On a framework panel',
  tender_lost: 'Bid and lost a public tender',
  job_posting: 'Hiring bid/proposal roles',
  website_visit: 'Visited our website',
  manual: 'Added manually',
};

export interface HandoffSource {
  sourceType: string;
  label: string;
  summary: string | null;
  strength: number;
  detail: Record<string, unknown> | null;
}

export interface SuggestedRoles {
  bidDesk: string[];
  decisionMaker: string[];
}

export interface HandoffRow {
  identityKey: string;
  name: string;
  domain: string | null;
  country: string | null;
  industry: string | null;
  employeeRange: string | null;
  sourceTypes: string[];
  sources: HandoffSource[];
  totalStrength: number;
  lastSeenAt: string;
  statuses: string[];
  matchedCompanyId: string | null;
  suggestedRoles: SuggestedRoles;
}

export interface SourcingHandoffResponse {
  count: number;
  /** Companies matching before the cap; older API builds omit it. */
  total?: number;
  limit?: number;
  /** Placeholder "companies" ("Zie bijgevoegd document") the API left out; older builds omit it. */
  hiddenPlaceholders?: number;
  /** Companies whose every source was retired (strength 0); kept in the ledger, hidden. Older builds omit it. */
  hiddenRetired?: number;
  /** Set when the request carried a search (`q`): the text, and how many companies it ran over. */
  query?: string;
  searched?: number;
  rows: HandoffRow[];
}

export interface TenderSyncResult {
  fetched: number;
  awardRows: number;
  upserted: number;
  matched: number;
  capped: boolean;
}

export interface RefreshSourcingResult {
  tenders: { written: number; found: number };
  frameworkPanels: { written: number; found: number };
  lostBids: { written: number; found: number };
  websiteVisits: { written: number; found: number };
  jobPostings: { written: number; found: number } | null;
}

export interface SyncTenderWinnersResult {
  awards: TenderSyncResult;
  sourced: RefreshSourcingResult;
}

export interface PullHiringResult {
  provider: string;
  written: number;
  found: number;
}

/** The provenance-tagged handoff list — one row per company, richest first. */
export function getSourcingHandoff(statuses?: string[], q?: string): Promise<SourcingHandoffResponse> {
  const params = new URLSearchParams();
  if (statuses && statuses.length > 0) params.set('statuses', statuses.join(','));
  // The server searches every company before it caps the list.
  if (q && q.trim()) params.set('q', q.trim());
  const qs = params.toString();
  return apiClient.get<SourcingHandoffResponse>(`/gtm-sourcing/handoff${qs ? `?${qs}` : ''}`);
}

/**
 * Free path: pull the latest public-tender awards (EU/TED), then roll frequent
 * winners we don't track into the sourcing list. Two calls because the pull and
 * the rollup are separate admin endpoints; the rollup also folds in website
 * visits, so it's the step that actually refreshes the list.
 */
export async function syncTenderWinners(): Promise<SyncTenderWinnersResult> {
  const awards = await apiClient.post<TenderSyncResult>('/tenders/sync', {});
  const sourced = await apiClient.post<RefreshSourcingResult>('/gtm-sourcing/refresh', {});
  return { awards, sourced };
}

/**
 * Paid path: pull companies hiring bid/proposal roles from TheirStack. BILLED per
 * job returned, so the pull is bounded (defaults ~25 jobs). Needs the TheirStack
 * key connected in Settings → Integrations.
 */
export function pullHiringCompanies(
  opts: { maxAgeDays?: number; pages?: number; perPage?: number } = {},
): Promise<PullHiringResult> {
  return apiClient.post<PullHiringResult>('/gtm-sourcing/job-postings', {
    provider: 'theirstack',
    maxAgeDays: opts.maxAgeDays ?? 30,
    pages: opts.pages ?? 1,
    perPage: opts.perPage ?? 25,
  });
}
